<template>
  <div
    v-if="isPreOrder"
    class="pre-order-message"
    :class="{ 'pre-order-message--is-coming-soon': isPreOrderLaunch }"
  >
    <div class="pre-order-message__container">
      <icon-info />

      <span
        v-if="isPreOrderLaunch"
        class="pre-order-message__message text-body-xs-regular"
        v-html="`${launchMessage}`"
      />

      <span
        v-else-if="hasPreOrderTag && !product?.metafields?.preOrderAvailableDate"
        class="pre-order-message__message text-body-xs-regular"
        v-html="`${genericMessage} ${preOrderClosingMessage}`"
      />

      <span
        v-else-if="isPreOrderAvailable || isPreOrderClosing"
        class="pre-order-message__message text-body-xs-regular"
        v-html="`${preOrderAvailableMessage} ${preOrderClosingMessage}`"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Pre order message (pre-order-message)
 * -----------------------------------------------------------------------------
 * Show pre-order message.
 * - Component settings: snippet / dynamic / async / scroll
 *
 * @param {Boolean|Object} product - Canvas formatted product object.
 *
 */
import { handlePreOrderProductCurrentDate, handlePreOrderProductFutureDate, handlePreOrderProductFutureDateTime, handlePreOrderProductPastDate } from '~/helpers/general'
import IconInfo from '~icons/general/info.svg'

export default {
  name: 'PreOrderMessage',

  components: {
    IconInfo,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  data() {
    return {

    }
  },

  computed: {

    /**
     * Is pre-order tag.
     */
    hasPreOrderTag() {
      return this.product.tags?.some((tag) => tag.includes('Pre-Order'))
    },

    /**
     * Is pre-order.
     */
    isPreOrder() {
      if (
        (this.hasPreOrderTag &&
          handlePreOrderProductCurrentDate(this.product?.metafields?.preOrderAvailableDate)) ||
        (this.hasPreOrderTag &&
          handlePreOrderProductPastDate(this.product?.metafields?.preOrderAvailableDate))
      ) {
        return false
      }

      return this.hasPreOrderTag
    },

    /**
     * Is pre-order available.
     */
    isPreOrderAvailable() {
      return handlePreOrderProductFutureDate(this.product?.metafields?.preOrderAvailableDate)
    },

    /**
     * Is pre-order closing.
     */
    isPreOrderClosing() {
      return handlePreOrderProductFutureDate(this.product?.metafields?.preOrderClosingDate)
    },

    /**
     * Is pre-order launch.
     */
    isPreOrderLaunch() {
      return this.hasPreOrderTag &&
        this.product?.metafields?.preOrderLaunchDate &&
        // eslint-disable-next-line max-len
        handlePreOrderProductFutureDateTime(this.product?.metafields?.preOrderLaunchDate, this.product?.currentDateTime)
    },

    /**
     * Is pre-order available message.
     */
    isPreOrderAvailableMessage() {
      return this.product?.metafields?.preOrderAvailableMessage
    },

    /**
     * Is pre-order closing message.
     */
    isPreOrderClosingMessage() {
      return this.product?.metafields?.preOrderClosingMessage
    },

    /**
     * Pre-order available message.
     */
    preOrderAvailableMessage() {
      if (this.isPreOrderAvailable) {
        const formatDate = `<b>${this.$formatDate('date_formats.day_singular_full_month_year', new Date(this.product?.metafields?.preOrderAvailableDate))}</b>`

        if (this.isPreOrderAvailableMessage) {
          return `${this.isPreOrderAvailableMessage} ${formatDate}.`
        }

        return this.$string(
          'product.pre_order.generic_available_message_date_html',
          {
            replace: {
              date: formatDate,
            },
          },
        )
      }

      return ''
    },

    /**
     * Pre-order closing message.
     */
    preOrderClosingMessage() {
      if (this.isPreOrderClosing) {
        const formatDate = `<b>${this.$formatDate('date_formats.day_singular_full_month_year', new Date(this.product?.metafields?.preOrderClosingDate))}</b>`

        if (this.isPreOrderClosingMessage) {
          return `${this.isPreOrderClosingMessage} ${formatDate}.`
        }

        return this.$string(
          'product.pre_order.generic_closing_message_date_html',
          {
            replace: {
              date: formatDate,
            },
          },
        )
      }

      if (this.product?.metafields?.preOrderClosingDate && !this.isPreOrderClosing) {
        const formatDate = `<b>${this.$formatDate('date_formats.day_singular_full_month_year', new Date(this.product?.metafields?.preOrderClosingDate))}</b>`

        return this.$string(
          'product.pre_order.generic_closing_ended_message_date_html',
          {
            replace: {
              date: formatDate,
            },
          },
        )
      }

      return ''
    },

    /**
     * Launch message string.
     */
    launchMessage() {
      const formatDate = `<b>${this.$formatDate('date_formats.day_singular_full_month_year', new Date(this.product?.metafields?.preOrderLaunchDate))}</b>`
      const dateCurrent = new Date(this.product?.metafields?.preOrderLaunchDate)
      const hour = dateCurrent.getHours().toString().padStart(2, '0')
      const minute = dateCurrent.getMinutes().toString().padStart(2, '0')
      const formatTime = `<b>${hour}:${minute}</b>`

      return this.$string(
        'product.pre_order.generic_launch_message_date_html',
        {
          replace: {
            time: formatTime,
            date: formatDate,
          },
        },
      )
    },

    /**
     * Generic message string.
     */
    genericMessage() {
      return this.$string('product.pre_order.generic_default_message_html')
    },
  },
}
</script>

<style lang="scss">
@import './pre-order-message';
</style>
